/**
 * The languages a person or a company can pick (MYAC-1522).
 *
 * - **Interface**: the app's own words, a personal choice in the profile.
 *   French or English only, the two locales the front ships. Unset = the
 *   browser's language.
 * - **Results**: what MAC writes (analyses, reports). A company choice, never a
 *   personal one, so every member's run reads alike (`utils/runLanguage.ts`).
 *   English and French (beta) today; the other languages are announced but not
 *   selectable: the EU's official languages, the ones MDR/IVDR documentation is
 *   written in, plus Norwegian and Turkish, common in European supply chains.
 */
export type InterfaceLanguage = 'fr' | 'en'

export const INTERFACE_LANGUAGES: readonly InterfaceLanguage[] = ['fr', 'en']

/** Results languages shown in the company language list but not selectable yet. */
export const COMING_SOON_RESULTS_LANGUAGES: readonly string[] = [
  'bg', 'cs', 'da', 'de', 'el', 'es', 'et', 'fi', 'ga', 'hr', 'hu', 'it',
  'lt', 'lv', 'mt', 'nb', 'nl', 'pl', 'pt', 'ro', 'sk', 'sl', 'sv', 'tr',
]

export function isInterfaceLanguage(value: unknown): value is InterfaceLanguage {
  return value === 'fr' || value === 'en'
}

/** The interface language a browser tag list asks for: French if it leads with French, English otherwise. */
export function interfaceLanguageFromTags(tags: readonly string[]): InterfaceLanguage {
  return tags[0]?.toLowerCase().startsWith('fr') ? 'fr' : 'en'
}

/**
 * A language's name as the reader says it ("allemand" in French, "German" in
 * English), capitalised for a list. Falls back to the code if the browser has
 * no name for it.
 */
export function languageName(code: string, uiLocale: string): string {
  try {
    const name = new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) ?? code
    return name.charAt(0).toLocaleUpperCase(uiLocale) + name.slice(1)
  } catch {
    return code
  }
}

/** The coming-soon results languages, named and sorted in the reader's language. */
export function comingSoonResultsLanguages(uiLocale: string): { code: string, name: string }[] {
  return COMING_SOON_RESULTS_LANGUAGES
    .map(code => ({ code, name: languageName(code, uiLocale) }))
    .sort((a, b) => a.name.localeCompare(b.name, uiLocale, { sensitivity: 'base' }))
}
